import React, { useState, useRef } from 'react';

interface VoiceActionItemRecorderProps {
  onVoiceResult: (actionItemData: any) => void;
  transcriptId?: string;
}

const VoiceActionItemRecorder: React.FC<VoiceActionItemRecorderProps> = ({ 
  onVoiceResult, 
  transcriptId 
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        processVoiceInput(audioBlob);
      };

      audioChunks.current = [];
      mediaRecorder.current.start(1000);
      setIsRecording(true);
      setError(null);
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Microphone access required');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
      }
    }
  };

  const processVoiceInput = async (audioBlob: Blob) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Transcribe audio
      const file = new File([audioBlob], 'voice-task.webm', { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('file', file);

      const transcribeResponse = await fetch('/api/transcribe', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!transcribeResponse.ok) {
        throw new Error(`Transcription failed: ${transcribeResponse.status}`);
      }

      const transcriptionResult = await transcribeResponse.json() as { text?: string; transcript?: string };
      const transcriptText = transcriptionResult.text || transcriptionResult.transcript || '';

      if (!transcriptText.trim()) {
        throw new Error('No speech detected in audio');
      }

      // Step 2: Parse transcript into action item using AI
      const parseResponse = await fetch('/api/action-items/voice-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcriptText,
          transcriptId
        }),
        credentials: 'include'
      });

      if (!parseResponse.ok) {
        throw new Error(`Failed to parse voice input: ${parseResponse.status}`);
      }

      const parseResult = await parseResponse.json() as { actionItem: any; originalTranscript: string };
      
      // Pass the parsed action item data to parent
      onVoiceResult({
        ...parseResult.actionItem,
        originalTranscript: parseResult.originalTranscript
      });

    } catch (err) {
      console.error('Voice processing error:', err);
      setError(err instanceof Error ? err.message : 'Voice processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="flex items-center space-x-2">
      {/* Voice Recording Button */}
      <button
        onClick={handleToggleRecording}
        disabled={isProcessing}
        className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
          isRecording 
            ? 'bg-red-500 hover:bg-red-600' 
            : isProcessing
            ? 'bg-blue-400 cursor-not-allowed'
            : 'bg-blue-500 hover:bg-blue-600'
        } text-white shadow-sm`}
        title={
          isProcessing 
            ? 'Processing voice...' 
            : isRecording 
            ? 'Click to stop recording' 
            : 'Click to start voice recording'
        }
      >
        {isProcessing ? (
          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
        ) : isRecording ? (
          // Blinking red dot when recording
          <div className="relative">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            <div className="absolute top-0 left-0 w-3 h-3 bg-white rounded-full animate-ping opacity-75"></div>
          </div>
        ) : (
          // Microphone icon when ready
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
          </svg>
        )}
      </button>

      {/* Status Text */}
      {(isRecording || isProcessing) && (
        <span className={`text-xs font-medium ${
          isRecording ? 'text-red-600' : 'text-blue-600'
        }`}>
          {isRecording ? 'Recording...' : 'Processing...'}
        </span>
      )}

      {/* Error Display */}
      {error && (
        <span className="text-xs text-red-600 font-medium">
          {error}
        </span>
      )}
    </div>
  );
};

export default VoiceActionItemRecorder; 